import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { NotificationDto } from '~/types'
import { useNotificationToast } from '~/composables/notificationToast/useNotificationToast'
import { useNotificationSound } from '~/composables/notificationToast/useNotificationSound'

export const useNotificationStore = defineStore('notifications', () => {
  const notifications = ref<NotificationDto[]>([])
  const unreadCount = ref(0)
  const loading = ref(false)
  const hasMore = ref(true)
  const offset = ref(0)
  const limit = 100

  let previousUnreadCount: number | null = null

  const fetchUnreadCount = async () => {
    try {
      const data = await $fetch<{ count: number }>('/api/notifications/unread-count')
      const newCount = data.count

      if (previousUnreadCount !== null && newCount > previousUnreadCount) {
        await triggerNewNotificationAlert(newCount - previousUnreadCount)
      }

      previousUnreadCount = newCount
      unreadCount.value = newCount
    } catch (e) {
      console.error('Failed to fetch unread count', e)
    }
  }

  const triggerNewNotificationAlert = async (newArrivalsCount: number) => {
    if (import.meta.server) return

    try {
      const data = await $fetch<NotificationDto[]>('/api/notifications', {
        query: { limit: newArrivalsCount, offset: 0 }
      })

      const { pushMany } = useNotificationToast()
      const { playNotificationSound } = useNotificationSound()

      const newArrivals = data.filter(n => !n.read_at).reverse()

      if (newArrivals.length === 0) return

      pushMany(newArrivals)
      playNotificationSound()
    } catch (e) {
      console.error('Failed to fetch latest notification for toast', e)
    }
  }

  const fetchNotifications = async (reset = false) => {
    if (loading.value) return
    if (reset) {
      offset.value = 0
      notifications.value = []
      hasMore.value = true
    }
    if (!hasMore.value) return

    loading.value = true
    try {
      const data = await $fetch<NotificationDto[]>('/api/notifications', {
        query: {
          limit,
          offset: offset.value
        }
      })
      if (data.length < limit) {
        hasMore.value = false
      }
      if (reset) {
        notifications.value = data
      } else {
        notifications.value = [...notifications.value, ...data]
      }
      offset.value += data.length
    } catch (e) {
      console.error('Failed to fetch notifications', e)
    } finally {
      loading.value = false
    }
  }

  const markAsRead = async (id: string) => {
    try {
      await $fetch(`/api/notifications/${id}/read`, { method: 'POST' })
      const notif = notifications.value.find(n => n.id === id)
      if (notif && !notif.read_at) {
        notif.read_at = new Date().toISOString()
        unreadCount.value = Math.max(0, unreadCount.value - 1)
        previousUnreadCount = unreadCount.value
      }
    } catch (e) {
      console.error('Failed to mark as read', e)
    }
  }

  const markAllAsRead = async () => {
    try {
      await $fetch('/api/notifications/read-all', { method: 'POST' })
      const now = new Date().toISOString()
      notifications.value.forEach(n => {
        if (!n.read_at) n.read_at = now
      })
      unreadCount.value = 0
      previousUnreadCount = 0
    } catch (e) {
      console.error('Failed to mark all as read', e)
    }
  }

  return {
    notifications,
    unreadCount,
    loading,
    hasMore,
    fetchUnreadCount,
    fetchNotifications,
    markAsRead,
    markAllAsRead
  }
})
